import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { FilteredProductPositionMultiActionHandlers } from "./useProductPositionMultiActionHandlers"

export interface UseProductPositionMultiActionsArgs
  extends Partial<FilteredProductPositionMultiActionHandlers> {
  isEditDisabled?: boolean
  isDuplicateDisabled?: boolean
  isCopyDisabled?: boolean
  isDeleteDisabled?: boolean
}

export function useProductPositionMultiActions({
  onEdit,
  isEditDisabled,
  onDuplicate,
  isDuplicateDisabled,
  onCopy,
  isCopyDisabled,
  onDelete,
  isDeleteDisabled
}: UseProductPositionMultiActionsArgs) {
  const { t } = useTranslation()

  const editAction: Action = useMemo(
    () => ({
      id: "product-position__multi-actions__edit",
      icon: IconName.EDIT,
      label: t("ProductPosition.MultiActions.Update"),
      disabled: isEditDisabled,
      onClick: onEdit ?? undefined
    }),
    [t, onEdit, isEditDisabled]
  )

  const duplicateAction = useMemo(
    () => ({
      id: "product-position__multi-actions__duplicate",
      icon: IconName.DUPLICATE,
      label: t("ProductPosition.MultiActions.Duplicate"),
      disabled: isDuplicateDisabled,
      onClick: onDuplicate ?? undefined
    }),
    [t, onDuplicate, isDuplicateDisabled]
  )

  const copyAction = useMemo(
    () => ({
      id: "product-position__actions__copy",
      icon: IconName.SHARE,
      label: t("ProductPosition.MultiActions.Copy"),
      disabled: isCopyDisabled,
      onClick: onCopy ?? undefined
    }),
    [t, onDuplicate, isDuplicateDisabled]
  )

  const deleteAction = useMemo(
    () => ({
      id: "product-position__multi-actions__delete",
      icon: IconName.BIN,
      label: t("ProductPosition.MultiActions.Delete"),
      isDanger: true,
      disabled: isDeleteDisabled,
      onClick: onDelete ?? undefined
    }),
    [t, onDelete, isDeleteDisabled]
  )

  const actions: Action[] = useMemo(
    () =>
      [editAction, duplicateAction, copyAction, deleteAction].filter(
        action => !!action.onClick
      ),
    [editAction, copyAction, duplicateAction, deleteAction]
  )

  return {
    actions,
    editAction,
    duplicateAction,
    copyAction,
    deleteAction
  }
}
